import { ResponseTranslation } from '../Translations';

/** Mirrors Finance.Models GlAccountRuleScope; values are persisted and start at 0. */
export enum GlAccountRuleScope {
  /** Rendición de gastos: fund and trip expense lines, keyed by concept + cost center. */
  ExpenseClaim = 0,
  /** Inventory legs, keyed by inventory type + cost center. */
  InventoryMovement = 1,
}

/** Mirrors Finance.Models GlAccountRuleRole; values are persisted and start at 0. */
export enum GlAccountRuleRole {
  Expense = 0,
  Inventory = 1,
  Offset = 2,
  Tax = 3,
}

/** The roles each scope accepts; the API refuses any other (expense claims post only expense or tax). */
export const glAccountRuleRolesByScope: Record<GlAccountRuleScope, GlAccountRuleRole[]> = {
  [GlAccountRuleScope.ExpenseClaim]: [GlAccountRuleRole.Expense, GlAccountRuleRole.Tax],
  [GlAccountRuleScope.InventoryMovement]: [
    GlAccountRuleRole.Inventory,
    GlAccountRuleRole.Offset,
    GlAccountRuleRole.Expense,
    GlAccountRuleRole.Tax,
  ],
};

/** The account a rule points at, snapshotted by the API from the chart of accounts. */
export interface GlAccountRuleAccount {
  reference: string | null;
  number: string | null;
  name: ResponseTranslation | null;
}

/**
 * GL account rule (#5782) — mirrors Finance.API GlAccountRuleDto. A null dimension means "any"; the most
 * specific active rule wins. Scope, role, company and the three dimensions are the rule's identity, so only
 * the account and the active flag can change after it is created.
 */
export interface GlAccountRule {
  id: string;
  scope: GlAccountRuleScope;
  role: GlAccountRuleRole;
  financialCompanyReference: string;
  /** Null = any cost center. A parent's rule is inherited by every child below it. */
  costCenterCode: string | null;
  /** Expense claim scope only. */
  conceptCode: string | null;
  /** Inventory movement scope only. */
  inventoryTypeReference: string | null;
  glAccount: GlAccountRuleAccount;
  isActive: boolean;
  createdBy: string | null;
  createdDate: string | null;
  modifiedBy: string | null;
  modifiedDate: string | null;
}

export interface GlAccountRuleInsert {
  scope: GlAccountRuleScope;
  role: GlAccountRuleRole;
  financialCompanyReference: string;
  costCenterCode: string | null;
  conceptCode: string | null;
  inventoryTypeReference: string | null;
  /** Only the reference is read; the API snapshots the number and name. */
  glAccount: { reference: string };
  isActive: boolean;
}

export interface GlAccountRuleUpdate {
  glAccountReference: string;
  isActive: boolean;
}

export interface GlAccountRuleSearchCommand {
  financialCompanyReference?: string | null;
  scopes?: GlAccountRuleScope[] | null;
  roles?: GlAccountRuleRole[] | null;
  costCenterCodes?: (string | null)[] | null;
  conceptCodes?: (string | null)[] | null;
  inventoryTypeReferences?: (string | null)[] | null;
  isActive?: boolean | null;
}

export interface GlAccountRuleResolveLine {
  /** Echoed back so results are matched by key, not by order. */
  key: string;
  costCenterCode: string | null;
  conceptCode: string | null;
  inventoryTypeReference: string | null;
}

export interface GlAccountRuleResolveCommand {
  scope: GlAccountRuleScope;
  role: GlAccountRuleRole;
  financialCompanyReference: string;
  lines: GlAccountRuleResolveLine[];
}

export interface GlAccountRuleResolution {
  key: string;
  glAccount: GlAccountRuleAccount | null;
  /** Null = no rule matched. */
  ruleReference: string | null;
  isResolved: boolean;
}

/** A rule that isn't saved (#5784). Set ruleReference when editing so the preview compares it with the others. */
export interface GlAccountRulePreviewCommand {
  ruleReference: string | null;
  scope: GlAccountRuleScope;
  role: GlAccountRuleRole;
  financialCompanyReference: string;
  costCenterCode: string | null;
  conceptCode: string | null;
  inventoryTypeReference: string | null;
}

export interface GlAccountRuleReplacement {
  ruleReference: string;
  /** The replaced rule's own cost center; null = any. */
  ruleCostCenterCode: string | null;
  glAccount: GlAccountRuleAccount;
  /** Where it stops winning. */
  costCenterCodes: string[];
}

/** What saving the rule would change, worked out by the API's resolver. */
export interface GlAccountRulePreview {
  totalCostCenters: number;
  /** Cost centers whose lines would take the rule. */
  costCenterCodes: string[];
  replaces: GlAccountRuleReplacement[];
  /** An active rule with the same combination; saving would be refused. */
  conflict: GlAccountRule | null;
}
